import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BLOCK_TYPE, POWERUP_LABELS, START_TILES } from '../game/constants';
import {
  addGarbageTile,
  addRandomTileToTiles,
  clearGarbageTile,
  createGrid,
  maxTileValue,
  moveTiles,
  movesAvailable,
  purgeExpiredBlocks,
  shuffleTiles,
  ensureBlockExpiry,
} from '../game/engine';

const SOLO_BOARD_POWERUPS = ['undo', 'switchTiles', 'deleteTile'];
const MAX_UNDO_HISTORY = 30;
const POWERUP_DROPS_ENABLED = false;

const buildBasePowerups = (isSolo) =>
  isSolo
    ? {
        freeze: 0,
        shuffle: 0,
        garbage: 0,
        clear: 0,
        colors: 0,
        targetedGarbage: 1,
        undo: 2,
        switchTiles: 2,
        deleteTile: 2,
      }
    : {
        freeze: 2,
        shuffle: 2,
        garbage: 2,
        clear: 2,
        colors: 2,
        targetedGarbage: 2,
        undo: 2,
        switchTiles: 2,
        deleteTile: 2,
      };

export const useGameLogic = ({ soloMode, setLastEvent }) => {
  const idCounter = useRef(0);
  const nextId = useCallback(() => {
    idCounter.current += 1;
    return idCounter.current;
  }, []);
  const TARGET_TILE = 2048;
  const lastMaxRef = useRef(0);
  const lastRoundIdRef = useRef(0);
  const basePowerups = useMemo(() => buildBasePowerups(soloMode), [soloMode]);

  const buildStartTiles = useCallback(() => {
    let tiles = [];
    for (let i = 0; i < START_TILES; i += 1) {
      tiles = addRandomTileToTiles(tiles, nextId);
    }
    return tiles;
  }, [nextId]);

  const [tiles, setTiles] = useState(() => buildStartTiles());
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(() => {
    const saved = Number(localStorage.getItem('2048-best'));
    return Number.isFinite(saved) ? saved : 48;
  });
  const [gameOver, setGameOver] = useState(false);
  const [powerups, setPowerups] = useState(() => ({ ...basePowerups }));
  const [frozenUntil, setFrozenUntil] = useState(0);
  const [noMergeStreak, setNoMergeStreak] = useState(0);
  const [, forceTick] = useState(0);
  const [, forceGarbageTick] = useState(0);
  const [hasWon, setHasWon] = useState(false);
  const [undoableMoves, setUndoableMoves] = useState(0);
  const [colorShiftMap, setColorShiftMap] = useState(null);
  const [colorShiftUntil, setColorShiftUntil] = useState(0);

  const isFrozen = Date.now() < frozenUntil;
  const freezeSeconds = isFrozen ? Math.max(0, Math.ceil((frozenUntil - Date.now()) / 1000)) : 0;
  const colorShiftActive = colorShiftUntil > Date.now() ? colorShiftMap : null;

  const undoHistoryRef = useRef([]);
  const syncUndoableMovesFromHistory = useCallback(() => {
    setUndoableMoves(undoHistoryRef.current.length);
  }, []);
  const lastServerSnapshotRef = useRef(null);

  useEffect(() => {
    // Keep the derived counter in sync even if a history mutation slipped through.
    setUndoableMoves(undoHistoryRef.current.length);
  }, [tiles]);

  useEffect(() => {
    if (!frozenUntil) return undefined;
    const timer = setInterval(() => {
      if (frozenUntil <= Date.now()) {
        setFrozenUntil(0);
      } else {
        forceTick((v) => v + 1);
      }
    }, 250);
    return () => clearInterval(timer);
  }, [frozenUntil]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTiles((prev) => purgeExpiredBlocks(prev, Date.now()));
      forceGarbageTick((v) => v + 1);
    }, 500);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!colorShiftUntil) return undefined;
    const timeout = setTimeout(() => {
      setColorShiftMap(null);
      setColorShiftUntil(0);
    }, Math.max(0, colorShiftUntil - Date.now()));
    return () => clearTimeout(timeout);
  }, [colorShiftUntil]);

  useEffect(() => {
    localStorage.setItem('2048-best', String(best));
  }, [best]);

  const spendPowerup = useCallback((type) => {
    let allowed = false;
    setPowerups((prev) => {
      if (!prev[type]) return prev;
      allowed = true;
      return { ...prev, [type]: prev[type] - 1 };
    });
    return allowed;
  }, []);

  const refundPowerup = useCallback((type) => {
    setPowerups((prev) => ({ ...prev, [type]: (prev[type] || 0) + 1 }));
  }, []);

  const buildSnapshot = useCallback(
    (overrides = {}) => ({
      tiles: (overrides.tiles || tiles).map((t) => ({ ...t, isNew: false, justMerged: false })),
      score: overrides.score ?? score,
      gameOver: overrides.gameOver ?? gameOver,
      hasWon: overrides.hasWon ?? hasWon,
      frozenUntil: overrides.frozenUntil ?? frozenUntil,
      noMergeStreak: overrides.noMergeStreak ?? noMergeStreak,
    }),
    [frozenUntil, gameOver, hasWon, noMergeStreak, score, tiles]
  );

  const pushServerSnapshot = useCallback(
    (snap) => {
      if (!snap) return;
      const normalized = buildSnapshot({
        tiles: snap.tiles || [],
        score: snap.score,
        gameOver: snap.gameOver,
        hasWon: snap.hasWon,
        frozenUntil: snap.frozenUntil,
        noMergeStreak: snap.noMergeStreak,
      });
      const next = [...undoHistoryRef.current, normalized];
      if (next.length > MAX_UNDO_HISTORY) next.shift();
      undoHistoryRef.current = next;
      syncUndoableMovesFromHistory();
    },
    [buildSnapshot, syncUndoableMovesFromHistory]
  );

  const pushSnapshot = useCallback(
    (overrides = {}) => {
      const snap = buildSnapshot(overrides);
      const next = [...undoHistoryRef.current, snap];
      if (next.length > MAX_UNDO_HISTORY) next.shift();
      undoHistoryRef.current = next;
      syncUndoableMovesFromHistory();
    },
    [buildSnapshot, syncUndoableMovesFromHistory]
  );

  const restoreSnapshot = useCallback(() => {
    if (!undoHistoryRef.current.length) return false;
    const next = [...undoHistoryRef.current];
    const snap = next.pop();
    undoHistoryRef.current = next;
    syncUndoableMovesFromHistory();
    setTiles(snap.tiles.map((t) => ({ ...t, isNew: false, justMerged: false })));
    setScore(snap.score);
    setGameOver(snap.gameOver);
    setHasWon(snap.hasWon);
    setFrozenUntil(snap.frozenUntil);
    setNoMergeStreak(snap.noMergeStreak);
    return true;
  }, [syncUndoableMovesFromHistory]);

  const grantPowerup = useCallback(
    (type) => {
      if (soloMode) return;
      setPowerups((prev) => ({ ...prev, [type]: (prev[type] || 0) + 1 }));
      if (setLastEvent) {
        setLastEvent((evt) => `${POWERUP_LABELS[type]} erhalten${evt ? ` - ${evt}` : ''}`);
      }
    },
    [setLastEvent, soloMode]
  );

  const handleLocalMove = useCallback(
    (direction) => {
      if (gameOver) return;

      if (Date.now() < frozenUntil) {
        if (setLastEvent) setLastEvent('Eingefroren');
        return;
      }

      const previousMax = maxTileValue(tiles);
      const now = Date.now();
      const cleanedTiles = purgeExpiredBlocks(tiles, now);
      const hadExpiry = cleanedTiles.length !== tiles.length;

      const { tiles: movedTiles, moved, gained, merges, maxTile } = moveTiles(cleanedTiles, direction, nextId);
      if (!moved) {
        if (hadExpiry) setTiles(cleanedTiles);
        return;
      }

      pushSnapshot({ tiles: cleanedTiles });

      const withTile = addRandomTileToTiles(movedTiles, nextId);
      const newScore = score + gained;

      setTiles(withTile);
      setScore(newScore);
      setBest((prev) => Math.max(prev, newScore));
      const reachedTarget = maxTile >= TARGET_TILE;
      setHasWon((prev) => prev || reachedTarget);
      setGameOver(reachedTarget || !movesAvailable(withTile));
      if (reachedTarget && setLastEvent) setLastEvent('2048 erreicht');
      lastMaxRef.current = Math.max(lastMaxRef.current, maxTileValue(withTile));

      const newNoMerge = merges > 0 ? 0 : noMergeStreak + 1;
      setNoMergeStreak(newNoMerge);

      if (POWERUP_DROPS_ENABLED) {
        const boardHasBlock = withTile.some((t) => t.type === BLOCK_TYPE);
        if (merges >= 2) grantPowerup('freeze');
        if (maxTile >= 256 && maxTile > previousMax) grantPowerup('garbage');
        if (maxTile >= 16 && maxTile > previousMax) grantPowerup('targetedGarbage');

        const rowAllEqual = (() => {
          const grid = createGrid(withTile);
          return grid.some(
            (row) =>
              row.every((cell) => cell && cell.type !== BLOCK_TYPE) &&
              row.every((cell, _, arr) => cell.value === arr[0].value)
          );
        })();
        if (rowAllEqual) grantPowerup('shuffle');

        if (boardHasBlock && newNoMerge >= 5) {
          grantPowerup('clear');
        if (setLastEvent) setLastEvent('Clear erhalten (5 Züge ohne Merge mit Blocker)');
        }
      }
    },
    [tiles, score, gameOver, nextId, frozenUntil, noMergeStreak, grantPowerup, pushSnapshot, setLastEvent]
  );

  const triggerLocalPowerup = useCallback(
    (type, payload) => {
      const soloAllowed = SOLO_BOARD_POWERUPS.includes(type);
      if (type === 'targetedGarbage') {
        // Auswahl erfolgt im UI; Verbrauch wird extern gesteuert.
        return false;
      }
      if (soloMode && !soloAllowed) return false;
      if (type === 'undo') {
        const hasHistory = undoHistoryRef.current.length > 0;
        const hasJoker = (powerups.undo || 0) > 0;
        if (!hasHistory || !hasJoker) {
        if (setLastEvent) setLastEvent('Nichts zum Rückgängig machen');
          return false;
        }
        const allowed = spendPowerup(type);
        if (!allowed) return false;
        const restored = restoreSnapshot();
        if (!restored) {
          refundPowerup(type);
          if (setLastEvent) setLastEvent('Nichts zum Rückgängig machen');
          return false;
        }
        if (setLastEvent) setLastEvent('Letzte Aktion rückgängig gemacht');
        return true;
      }
      if (type === 'switchTiles') {
        const { first, second } = payload || {};
        if (!first || !second) return false;
        const tileA = tiles.find((t) => t.row === first.row && t.col === first.col);
        const tileB = tiles.find((t) => t.row === second.row && t.col === second.col);
        if (!tileA || !tileB) return false;
        if (tileA.value === tileB.value) {
          if (setLastEvent) setLastEvent('Gleiche Werte werden nicht getauscht');
          return false;
        }
        const allowed = spendPowerup(type);
        if (!allowed) return false;
        const updatedTiles = tiles.map((t) => ({ ...t, isNew: false, justMerged: false }));
        const idxA = updatedTiles.findIndex((t) => t.row === first.row && t.col === first.col);
        const idxB = updatedTiles.findIndex((t) => t.row === second.row && t.col === second.col);
        updatedTiles[idxA] = { ...updatedTiles[idxA], row: second.row, col: second.col };
        updatedTiles[idxB] = { ...updatedTiles[idxB], row: first.row, col: first.col };
        pushSnapshot();
        setTiles(updatedTiles);
        setGameOver(false);
        if (setLastEvent) setLastEvent('Tiles vertauscht');
        return true;
      }
      if (type === 'deleteTile') {
        const { row, col } = payload || {};
        if (row == null || col == null) return false;
        const allowed = spendPowerup(type);
        if (!allowed) return false;
        const filtered = tiles.filter((t) => !(t.row === row && t.col === col));
        if (filtered.length === tiles.length) {
          refundPowerup(type);
          return false;
        }
        pushSnapshot();
        setTiles(filtered.map((t) => ({ ...t, isNew: false, justMerged: false })));
        setGameOver(false);
        if (setLastEvent) setLastEvent('Tile gelöscht');
        return true;
      }

      const allowed = spendPowerup(type);
      if (!allowed) return false;

      if (setLastEvent) setLastEvent(`${POWERUP_LABELS[type]} genutzt`);

      if (type === 'freeze') {
        setFrozenUntil(Date.now() + 10000);
      } else if (type === 'shuffle') {
        pushSnapshot();
        setTiles((prev) => shuffleTiles(prev));
      } else if (type === 'garbage') {
        pushSnapshot();
        setTiles((prev) => addGarbageTile(prev, nextId));
      } else if (type === 'clear') {
        pushSnapshot();
        setTiles((prev) => clearGarbageTile(prev));
      }
      setGameOver(false);
      return true;
    },
    [nextId, powerups, pushSnapshot, refundPowerup, restoreSnapshot, setLastEvent, soloMode, spendPowerup, tiles]
  );

  const resetLocalGame = useCallback(
    (mode = soloMode) => {
      const base = mode === soloMode ? basePowerups : buildBasePowerups(mode);
      const freshTiles = buildStartTiles().map((t) => ({ ...t, isNew: false, justMerged: false }));
      setTiles(freshTiles);
      setScore(0);
      setGameOver(false);
      setHasWon(false);
      setPowerups({ ...base });
      setFrozenUntil(0);
      setNoMergeStreak(0);
      setColorShiftMap(null);
      setColorShiftUntil(0);
      undoHistoryRef.current = [];
      setUndoableMoves(0);
      lastMaxRef.current = 0;
      lastRoundIdRef.current = 0;
      lastServerSnapshotRef.current = null;
      if (setLastEvent) setLastEvent('');
    },
    [basePowerups, buildStartTiles, setLastEvent, soloMode]
  );

  const applyServerState = useCallback(
    (you, now) => {
      const incomingRoundId = you?.roundId || 0;
      if (incomingRoundId !== lastRoundIdRef.current) {
        lastRoundIdRef.current = incomingRoundId;
        undoHistoryRef.current = [];
        setUndoableMoves(0);
        lastMaxRef.current = 0;
        lastServerSnapshotRef.current = null;
      }
      if (lastServerSnapshotRef.current && (you.undoableMoves || 0) > undoHistoryRef.current.length) {
        pushServerSnapshot(lastServerSnapshotRef.current);
      }
      const tilesWithExpiry = (you.tiles || []).map((t) => ensureBlockExpiry(t, now));
      setTiles(tilesWithExpiry);
      const maxValue = maxTileValue(tilesWithExpiry);
      setHasWon(maxValue >= TARGET_TILE);
      setScore(you.score || 0);
      setBest(you.best || 0);
      setColorShiftMap(you.colorShiftMap || null);
      setColorShiftUntil(you.colorShiftUntil || 0);
      setPowerups((prev) => {
        const base = { ...basePowerups };
        const merged = you.powerups ? { ...base, ...you.powerups } : base;
        merged.targetedGarbage = Math.max(merged.targetedGarbage || 0, base.targetedGarbage || 1);
        if (maxValue >= 16 && maxValue > lastMaxRef.current) {
          merged.targetedGarbage = (merged.targetedGarbage || 0) + 1;
        }
        lastMaxRef.current = Math.max(lastMaxRef.current, maxValue);
        return merged;
      });
      setFrozenUntil(you.frozenUntil || 0);
      setNoMergeStreak(you.noMergeStreak || 0);
      setGameOver(!!you.gameOver);
      undoHistoryRef.current = undoHistoryRef.current.slice(0, you.undoableMoves || undoHistoryRef.current.length);
      syncUndoableMovesFromHistory();
      lastServerSnapshotRef.current = {
        tiles: tilesWithExpiry.map((t) => ({ ...t })),
        score: you.score || 0,
        gameOver: !!you.gameOver,
        frozenUntil: you.frozenUntil || 0,
        noMergeStreak: you.noMergeStreak || 0,
        colorShiftMap: you.colorShiftMap || null,
        colorShiftUntil: you.colorShiftUntil || 0,
      };
      if (setLastEvent) setLastEvent(you.lastEvent || '');
    },
    [basePowerups, pushServerSnapshot, setLastEvent, syncUndoableMovesFromHistory]
  );

  return {
    tiles,
    score,
    best,
    gameOver,
    powerups,
    frozenUntil,
    isFrozen,
    freezeSeconds,
    hasWon,
    handleLocalMove,
    triggerLocalPowerup,
    deleteTileImmediate: (row, col) => {
      if (row == null || col == null) return false;
      let removed = false;
      setTiles((prev) => {
        const filtered = prev.filter((t) => !(t.row === row && t.col === col));
        removed = filtered.length !== prev.length;
        return filtered.map((t) => ({ ...t, isNew: false, justMerged: false }));
      });
      if (removed) {
        setGameOver(false);
      }
      return removed;
    },
    switchTilesImmediate: (first, second) => {
      if (!first || !second) return false;
      const a = tiles.find((t) => t.row === first.row && t.col === first.col);
      const b = tiles.find((t) => t.row === second.row && t.col === second.col);
      if (!a || !b) return false;
      if (a.value === b.value) {
        if (setLastEvent) setLastEvent('Gleiche Werte werden nicht getauscht');
        return false;
      }
      pushSnapshot();
      let swapped = false;
      setTiles((prev) => {
        const updated = prev.map((t) => ({ ...t, isNew: false, justMerged: false }));
        const idxA = updated.findIndex((t) => t.row === first.row && t.col === first.col);
        const idxB = updated.findIndex((t) => t.row === second.row && t.col === second.col);
        if (idxA === -1 || idxB === -1) return prev;
        const tempRow = updated[idxA].row;
        const tempCol = updated[idxA].col;
        updated[idxA] = { ...updated[idxA], row: updated[idxB].row, col: updated[idxB].col };
        updated[idxB] = { ...updated[idxB], row: tempRow, col: tempCol };
        swapped = true;
        return updated;
      });
      if (swapped) setGameOver(false);
      return swapped;
    },
    colorShiftMap: colorShiftActive,
    resetLocalGame,
    applyServerState,
    undoableMoves,
    consumePowerup: (type) => {
      let allowed = false;
      setPowerups((prev) => {
        if (!prev[type]) return prev;
        allowed = true;
        return { ...prev, [type]: prev[type] - 1 };
      });
      return allowed;
    },
  };
};
